import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
	Button,
	Form,
	FormGroup,
	Row,
	Col,
	Input,
	FormFeedback,
	Label,
} from "reactstrap";
import Select from "react-select";
import organizationalStructureService from "services/OrganizationalStructureService";
import ataEncaminhamentosService from "services/AtaEncaminhamentosService";
import userService from "services/UserService";
import ModalChangePrazo from "./ModalChangePrazo";
import {
	Calendar,
	CheckSquare,
	FloppyDisk,
	SignOut,
	Square,
} from "@phosphor-icons/react";
import { Checkbox } from "@mui/material";

export const statusOptions = [
	{
		value: "ANDAMENTO",
		label: "Em Andamento",
		icon: "bx bx-right-arrow",
		color: "primary",
	},
	{
		value: "STANDBY",
		label: "Standby",
		icon: "stop-circle",
		color: "bg-dark",
	},
	{ value: "OK", label: "Ok", icon: "like", color: "success" },
	{ value: "CANCELADO", label: "Cancelado", icon: "block", color: "danger" },
];

const ModalCreateEdit = ({ show, onCloseClick, data, changePrazoItem }) => {
	const id = data?.id;
	const [departamentos, setDepartamentos] = useState([]);
	const [usuarios, setUsuarios] = useState([]);

	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			ata_id: data?.ata_id || data || "",
			organizational_structure_id:
				data?.organizational_structure_id || "",
			responsible_id: data?.responsible_id || "",
			deadline: data?.deadline || "",
			referrals: data?.referrals || "",
			status: id ? data?.status || "ANDAMENTO" : "ANDAMENTO",
			periodic: data?.periodic === "1" || data?.periodic === 1 ? 1 : 0,
		},

		validationSchema: Yup.object({
			deadline: Yup.string().required("Por favor, informe o prazo!"),
			referrals: Yup.string().required("Por favor, informe a descrição!"),
			organizational_structure_id: Yup.string().required(
				"Por favor, informe o departamento!",
			),
			responsible_id: Yup.string().required(
				"Por favor, informe o responsável!",
			),
			status: Yup.string().required("Por favor, selecione o status!"), // Validação adicionada
		}),

		onSubmit: async (values) => {
			try {
				if (id) {
					await ataEncaminhamentosService.put(id, values);
				} else {
					await ataEncaminhamentosService.post(values);
				}
				onCloseClick();
			} catch (error) {
				console.log(error);
				// Opcional: adicionar lógica para exibir erros de submissão
			}
		},
	});

	const customStyles = {
		option: (provided, state) => ({
			...provided,
			color: state.isSelected ? "blue" : "black",
			backgroundColor: "white",
			"&:hover": {
				backgroundColor: "#556ee6",
			},
		}),
		control: (provided, state) => ({
			...provided,
			borderColor:
				(validation.touched.organizational_structure_id &&
					validation.errors.organizational_structure_id) ||
				(validation.touched.status && validation.errors.status)
					? "red"
					: provided.borderColor,
			boxShadow:
				(validation.touched.organizational_structure_id &&
					validation.errors.organizational_structure_id) ||
				(validation.touched.status && validation.errors.status)
					? "0 0 0 0.2rem rgba(255, 0, 0, 0.25)"
					: provided.boxShadow,
			"&:hover": {
				borderColor:
					(validation.touched.organizational_structure_id &&
						validation.errors.organizational_structure_id) ||
					(validation.touched.status && validation.errors.status)
						? "red"
						: provided.borderColor,
			},
		}),
		// Adicione mais estilos personalizados conforme necessário
	};

	async function getDepartamentos() {
		try {
			const response = await organizationalStructureService.get();

			const options = response.map((item) => ({
				value: item.id,
				label: item.name,
			}));
			setDepartamentos(options);
		} catch (error) {
			console.log(error);
		}
	}

	async function getUsuarios() {
		try {
			const response = await userService.get();
			const funcoes = response.map((item) => ({
				value: item.id,
				label: item.lastname,
			}));
			setUsuarios(funcoes);
		} catch (error) {
			console.log(error);
		}
	}

	useEffect(() => {
		if (show) {
			getDepartamentos();
			getUsuarios();
		}
	}, [show]);

	return (
		<>
			<Modal
				size="sm"
				isOpen={show}
				toggle={onCloseClick}
				centered={true}
				style={{ minWidth: 800 }}
				zIndex={1500}
			>
				<div className="modal-content">
					<ModalBody className="px-4 py-3 text-center">
						<Col md="12">
							<Form
								className="needs-validation"
								onSubmit={(e) => {
									e.preventDefault();
									validation.handleSubmit();
								}}
							>
								<h4 className="text-center my-4">
									{id
										? "Editar Encaminhamento"
										: "Novo Encaminhamento"}
								</h4>

								{/* Campo Descrição */}
								<Row>
									<Col md="12">
										<FormGroup className="mb-3">
											<Label htmlFor="referrals">
												Descrição{" "}
												<span style={{ color: "red" }}>
													*
												</span>
											</Label>
											<Input
												name="referrals"
												type="textarea"
												placeholder="Descrição do Encaminhamento"
												onChange={
													validation.handleChange
												}
												onBlur={validation.handleBlur}
												value={
													validation.values
														.referrals || ""
												}
												invalid={
													validation.touched
														.referrals &&
													Boolean(
														validation.errors
															.referrals,
													)
												}
											/>
											{validation.touched.referrals &&
											validation.errors.referrals ? (
												<FormFeedback type="invalid">
													{
														validation.errors
															.referrals
													}
												</FormFeedback>
											) : null}
										</FormGroup>
									</Col>
								</Row>

								{/* Campo Responsável e Departamento */}
								<Row>
									<Col md="6">
										<FormGroup className="mb-3">
											<Label htmlFor="responsible_id">
												Responsável{" "}
												<span style={{ color: "red" }}>
													*
												</span>
											</Label>
											<Select
												styles={customStyles}
												placeholder="Selecione o Responsável"
												onChange={(selectedOption) => {
													const selectedValue =
														selectedOption
															? selectedOption.value
															: "";
													validation.setFieldValue(
														"responsible_id",
														selectedValue,
													);
												}}
												onBlur={() =>
													validation.setFieldTouched(
														"responsible_id",
														true,
													)
												}
												value={
													usuarios.find(
														(option) =>
															option.value ===
															validation.values
																.responsible_id,
													) || null
												}
												options={usuarios}
												isInvalid={
													validation.touched
														.responsible_id &&
													Boolean(
														validation.errors
															.responsible_id,
													)
												}
											/>
											{validation.touched
												.responsible_id &&
											validation.errors.responsible_id ? (
												<div
													style={{
														color: "red",
														marginTop: 5,
													}}
												>
													{
														validation.errors
															.responsible_id
													}
												</div>
											) : null}
										</FormGroup>
									</Col>
									<Col md="6">
										<FormGroup className="mb-3">
											<Label htmlFor="organizational_structure_id">
												Departamento{" "}
												<span style={{ color: "red" }}>
													*
												</span>
											</Label>
											<Select
												styles={customStyles}
												placeholder="Selecione"
												onChange={(selectedOption) => {
													const selectedValue =
														selectedOption
															? selectedOption.value
															: "";
													validation.setFieldValue(
														"organizational_structure_id",
														selectedValue,
													);
												}}
												onBlur={() =>
													validation.setFieldTouched(
														"organizational_structure_id",
														true,
													)
												}
												value={
													departamentos.find(
														(option) =>
															option.value ===
															validation.values
																.organizational_structure_id,
													) || null
												}
												options={departamentos}
												isInvalid={
													validation.touched
														.organizational_structure_id &&
													Boolean(
														validation.errors
															.organizational_structure_id,
													)
												}
											/>
											{validation.touched
												.organizational_structure_id &&
											validation.errors
												.organizational_structure_id ? (
												<div
													style={{
														color: "red",
														marginTop: 5,
													}}
												>
													{
														validation.errors
															.organizational_structure_id
													}
												</div>
											) : null}
										</FormGroup>
									</Col>
								</Row>

								{/* Campo Prazo, Status e Botão "Alterar Prazo" */}
								<Row style={{ alignItems: "center" }}>
									{/* Campo Status */}
									<Col md="6">
										<FormGroup className="mb-3">
											<Label htmlFor="status">
												Status{" "}
												<span style={{ color: "red" }}>
													*
												</span>
											</Label>
											<Select
												styles={customStyles}
												placeholder="Selecione o Status"
												onChange={(selectedOption) => {
													const selectedValue =
														selectedOption
															? selectedOption.value
															: "";
													validation.setFieldValue(
														"status",
														selectedValue,
													);
												}}
												onBlur={() =>
													validation.setFieldTouched(
														"status",
														true,
													)
												}
												value={
													statusOptions.find(
														(option) =>
															option.value ===
															validation.values
																.status,
													) || null
												}
												options={statusOptions}
												isInvalid={
													validation.touched.status &&
													Boolean(
														validation.errors
															.status,
													)
												}
											/>
											{validation.touched.status &&
											validation.errors.status ? (
												<div
													style={{
														color: "red",
														marginTop: 5,
													}}
												>
													{validation.errors.status}
												</div>
											) : null}
										</FormGroup>
									</Col>

									{/* Campo Prazo */}
									<Col md={id ? 3 : 6}>
										<FormGroup className="mb-3">
											<Label htmlFor="deadline">
												Prazo{" "}
												<span style={{ color: "red" }}>
													*
												</span>
											</Label>
											<Input
												name="deadline"
												type="date"
												placeholder="Prazo"
												className="form-control"
												onChange={
													validation.handleChange
												}
												onBlur={validation.handleBlur}
												value={
													validation.values
														.deadline || ""
												}
												invalid={
													validation.touched
														.deadline &&
													Boolean(
														validation.errors
															.deadline,
													)
												}
											/>
											{validation.touched.deadline &&
											validation.errors.deadline ? (
												<FormFeedback type="invalid">
													{validation.errors.deadline}
												</FormFeedback>
											) : null}
										</FormGroup>
									</Col>

									{/* Botão "Alterar Prazo" (condicional) */}
									{id && (
										<Col md="3">
											<Label>&nbsp;</Label>
											<Button
												style={{
													background: "none",
													cursor: "pointer",
													color: "black",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													gap: 2,
													width: "100%", // Ocupa todo o espaço da coluna
													borderColor:
														"hsl(0, 0%, 80%)",
												}}
												onClick={() =>
													changePrazoItem(data)
												}
											>
												<Calendar size={16} />
												Alterar prazo
											</Button>
											{/* Adiciona um espaço simulando validação */}
											<div style={{ height: 18 }}></div>
										</Col>
									)}
								</Row>

								{/* Campo "Ação Periódica" em uma nova linha */}
								<Row>
									<Col md="12">
										<FormGroup className="mb-3">
											<Checkbox
												id="periodic"
												name="periodic"
												defaultChecked={
													!!validation.values.periodic
												}
												onChange={(event) => {
													const value = event.target
														.checked
														? 1
														: 0;
													validation.setFieldValue(
														"periodic",
														value,
													);
												}}
												icon={<Square size={16} />}
												checkedIcon={
													<CheckSquare size={16} />
												}
											/>
											<Label
												className="form-check-label"
												htmlFor="periodic"
												style={{ marginLeft: 10 }}
											>
												Ação periódica
											</Label>
										</FormGroup>
									</Col>
								</Row>

								{/* Botões de Submissão e Fechamento */}
								<Row className="py-1">
									<div className="hstack gap-2 justify-content-center mb-0">
										<Button
											type="submit"
											disabled={validation.isSubmitting}
											style={{
												background: "none",
												padding: 10,
												cursor: "pointer",
												color: "black",
												marginLeft: 10,
												width: 150,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												gap: 2,
											}}
										>
											{validation.isSubmitting ? (
												<span
													className="spinner-border spinner-border-sm mr-1"
													style={{ marginRight: 10 }}
												></span>
											) : null}
											<FloppyDisk size={16} /> Salvar
										</Button>
										<Button
											type="button"
											onClick={onCloseClick}
											style={{
												background: "none",
												padding: 10,
												cursor: "pointer",
												color: "black",
												marginLeft: 10,
												width: 150,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												gap: 2,
											}}
										>
											<SignOut size={16} /> Fechar
										</Button>
									</div>
								</Row>
							</Form>
						</Col>
					</ModalBody>
				</div>
			</Modal>
		</>
	);
};

ModalCreateEdit.propTypes = {
	show: PropTypes.bool,
	onCloseClick: PropTypes.func,
	data: PropTypes.any,
	changePrazoItem: PropTypes.func,
};

export default ModalCreateEdit;
